import { useEffect, useId, useRef, useState } from 'react';

interface ToolbarMoreProps {
  onAddKeyDate: () => void;
  onManageCampaigns: () => void;
}

/**
 * The calendar toolbar's quieter actions on a phone, behind one More button,
 * so the row keeps Plan post full width. A disclosure, not an ARIA menu, like
 * the history tile's: two ordinary buttons in Tab order, Escape closes it and
 * returns focus to the trigger, a press outside closes it, and picking either
 * action closes it before its dialog opens.
 */
export function ToolbarMore({ onAddKeyDate, onManageCampaigns }: ToolbarMoreProps) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!isOpen) return;

    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setIsOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      setIsOpen(false);
      triggerRef.current?.focus();
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  function pick(action: () => void) {
    setIsOpen(false);
    action();
  }

  return (
    <div className="cal-more" ref={rootRef}>
      <button
        ref={triggerRef}
        className="button button--ghost cal-more__trigger"
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((open) => !open)}
      >
        More
      </button>
      <div className="cal-more__panel" id={panelId} hidden={!isOpen}>
        <button className="cal-more__item" type="button" onClick={() => pick(onAddKeyDate)}>
          Add event
        </button>
        <button className="cal-more__item" type="button" onClick={() => pick(onManageCampaigns)}>
          Manage campaigns
        </button>
      </div>
    </div>
  );
}
